import { useQuery } from "react-query";

import { useAppProperties } from "./useAppProperties";
import { AppProperties } from "@/api";

type Location = "HeadEnd" | "BodyEnd";

const fetchCustomScripts = async (url: string) => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch custom script`);
    }
    return await response.text();
  } catch (error) {
    throw new Error(
      error instanceof Error ? error.message : "Failed to fetch custom script",
    );
  }
};

// Get the URL for the custom scripts from the app property
const getUrl = (
  location: Location,
  data?: AppProperties,
): string | undefined => {
  try {
    const property = data?.WebCustomScripts;
    const config = JSON.parse(!property || property === "" ? "{}" : property);
    return config[location];
  } catch (error) {
    return undefined;
  }
};

// Convert the string of scripts into script elements
const getScriptElements = (scriptsString: string) => {
  const tempEl = document.createElement("div");
  tempEl.innerHTML = scriptsString;
  const scripts = tempEl.getElementsByTagName("script");

  const scriptElements: HTMLScriptElement[] = [];

  Array.from(scripts).forEach((originalScript) => {
    const script = document.createElement("script");

    // Copy all attributes
    Array.from(originalScript.attributes).forEach((attr) => {
      script.setAttribute(attr.name, attr.value);
    });

    if (originalScript.src) {
      script.src = originalScript.src;
    } else {
      script.innerHTML = originalScript.innerHTML;
    }

    scriptElements.push(script);
  });

  return scriptElements;
};

export const useCustomScripts = ({ location }: { location: Location }) => {
  const { data } = useAppProperties();

  const url = getUrl(location, data);

  return useQuery(
    ["customScript", url],
    async () => {
      if (url) {
        const scriptsString = await fetchCustomScripts(url);

        if (scriptsString) {
          return getScriptElements(scriptsString);
        }
      }
      return undefined;
    },
    { enabled: !!url, staleTime: Infinity },
  );
};
